import { OpsPaperworkQuickbooksSection } from "./OpsPaperworkQuickbooksSection";
import { OpsOwnershipDigestSection } from "./OpsOwnershipDigestSection";
import { OpsInboundWebhooksSection } from "./OpsInboundWebhooksSection";
import { OpsIdeaRoomImportSection } from "./OpsIdeaRoomImportSection";
import { OpsSenseiImportSection } from "./OpsSenseiImportSection";
import { OpsPaperworkFastfieldSection } from "./OpsPaperworkFastfieldSection";
import { OpsPaperworkDriveSettingsSection } from "./OpsPaperworkDriveSettingsSection";
import { OpsSettingsCollapse } from "./OpsSettingsCollapse";
import { normalizeOwnershipDigestSchedule } from "@/lib/messages/ownershipDigestModel";

export function OpsPaperworkSettingsTab(props: {
  settings: Record<string, unknown>;
  setSettings: (s: Record<string, unknown>) => void;
  setMsg: (s: string) => void;
  onToggleSetting: (key: string, value: boolean) => Promise<void>;
  onPatchUrl: (key: string, value: string | null) => Promise<void>;
  onRotateInventoryHostWebhookToken: () => Promise<void> | void;
  webhookTokenBusy?: boolean;
  onSeedRules: () => Promise<void>;
  onProbeStubs: () => Promise<void>;
  onFastfieldSync: () => Promise<void>;
  fastfieldSubmissions: Array<{
    id: string;
    external_submission_id: string;
    deal_id: string | null;
    created_at: string;
    raw: Record<string, unknown>;
  }>;
  fastfieldLoading: boolean;
}) {
  return (
    <div className="min-w-0 space-y-3 text-sm">
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={Boolean(props.settings.inventory_host_auto_create_on_customer_order_submitted)}
          onChange={(e) => void props.onToggleSetting("inventory_host_auto_create_on_customer_order_submitted", e.target.checked)}
        />
        Auto-create deal + Kanban unit on inventory host customer order submitted (webhook)
      </label>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={Boolean(props.settings.inventory_host_auto_archive_on_customer_order_cancelled)}
          onChange={(e) => void props.onToggleSetting("inventory_host_auto_archive_on_customer_order_cancelled", e.target.checked)}
        />
        Auto-archive deal on customer order cancelled
      </label>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={Boolean(props.settings.inventory_host_auto_delivery_paperwork_on_delivered)}
          onChange={(e) => void props.onToggleSetting("inventory_host_auto_delivery_paperwork_on_delivered", e.target.checked)}
        />
        Auto mark delivery milestone row on customer order delivered
      </label>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={Boolean(props.settings.inventory_host_erp_production_enabled)}
          onChange={(e) => void props.onToggleSetting("inventory_host_erp_production_enabled", e.target.checked)}
        />
        Admit host orders into manufacturing ERP when the shop matches a plant (no deal or kanban)
      </label>
      <OpsSettingsCollapse title="Inbound webhooks">
        <OpsInboundWebhooksSection
          token={
            typeof props.settings.inventory_host_inbound_webhook_token === "string"
              ? props.settings.inventory_host_inbound_webhook_token
              : null
          }
          busy={Boolean(props.webhookTokenBusy)}
          onRotate={() => void props.onRotateInventoryHostWebhookToken()}
        />
      </OpsSettingsCollapse>
      <OpsSettingsCollapse title="Import JSON">
        <OpsIdeaRoomImportSection setMsg={props.setMsg} />
        <OpsSenseiImportSection setMsg={props.setMsg} />
      </OpsSettingsCollapse>
      <OpsSettingsCollapse title="Google Workspace / Drive" defaultOpen>
        <OpsPaperworkDriveSettingsSection
          settings={props.settings}
          setSettings={props.setSettings}
          onToggleSetting={props.onToggleSetting}
          setMsg={props.setMsg}
        />
      </OpsSettingsCollapse>
      <OpsSettingsCollapse title="Ownership digest (Layer A)">
        <OpsOwnershipDigestSection
          digestEnabled={Boolean(props.settings.ops_ownership_digest_enabled)}
          onToggleEnabled={(v) => props.onToggleSetting("ops_ownership_digest_enabled", v)}
          scheduleEncouragingNote={
            normalizeOwnershipDigestSchedule(props.settings.ownership_digest_schedule).encouraging_note ?? ""
          }
          scheduleLocalSendTime={
            normalizeOwnershipDigestSchedule(props.settings.ownership_digest_schedule).local_send_time ?? ""
          }
          lastSentAt={normalizeOwnershipDigestSchedule(props.settings.ownership_digest_schedule).last_sent_at ?? null}
          lastDigestDay={normalizeOwnershipDigestSchedule(props.settings.ownership_digest_schedule).last_digest_day ?? null}
        />
      </OpsSettingsCollapse>
      <OpsSettingsCollapse title="Economics export">
        <label className="block text-xs text-gray-600">
          Webhook URL (optional)
          <input
            className="mt-1 block w-full rounded border border-gray-300 px-2 py-1 font-mono text-sm"
            placeholder="https://..."
            defaultValue={(props.settings.ops_economics_export_webhook_url as string) ?? ""}
            onBlur={(e) => void props.onPatchUrl("ops_economics_export_webhook_url", e.target.value.trim() || null)}
          />
        </label>
      </OpsSettingsCollapse>
      <div className="flex flex-wrap gap-2">
        <button type="button" className="rounded border px-3 py-1 text-sm" onClick={() => void props.onSeedRules()}>
          Seed default manifest rules
        </button>
        <button type="button" className="rounded border px-3 py-1 text-sm" onClick={() => void props.onProbeStubs()}>
          Probe Drive / digest
        </button>
      </div>
      <OpsSettingsCollapse title="FastField evidence loop">
        <OpsPaperworkFastfieldSection
          fastfieldLoading={props.fastfieldLoading}
          onFastfieldSync={props.onFastfieldSync}
          fastfieldSubmissions={props.fastfieldSubmissions}
        />
      </OpsSettingsCollapse>
      <OpsSettingsCollapse title="QuickBooks retail">
        <OpsPaperworkQuickbooksSection setMsg={props.setMsg} />
      </OpsSettingsCollapse>
    </div>
  );
}
